/**
 * Barrel + legacy-bridge registration for every Pinia store in the Vuex -> Pinia migration.
 *
 * 1. Re-exports every store composable (all 44 converted modules + the 3 Phase-1 stores:
 *    `app`, `preferences`, `notifications`) so callers can do
 *    `import { useAuthStore, useBotsStore } from '@/stores'`.
 * 2. Exposes `registerAllLegacyStores()`, which registers every legacy vuex namespace (except
 *    `app`/`preferences`/`notifications`, which `src/stores/legacyBridge.ts` already registers
 *    eagerly on import) into the `legacyBridge` registry, so `commitLegacyMutation()` /
 *    `resolveLegacyNamespace()` can resolve ANY legacy namespace once this module has been
 *    imported and the function called.
 *
 * IMPORTANT: this file must stay import-safe — it must NEVER call any `useXStore()` at module
 * top level (that would instantiate a store before Pinia is installed / before `app.use(pinia)`
 * has run in `main.ts`). It only ever *references* the composable functions themselves (which
 * have the exact `() => any` shape `registerLegacyStores` expects), and only *calls* them lazily,
 * inside `legacyBridge`'s own resolution functions (`resolveLegacyNamespace`,
 * `commitLegacyMutation`), long after Pinia is installed.
 */

// --- Store re-exports (barrel) --------------------------------------------------------------
// Phase 1 (already registered with the legacy bridge by `./legacyBridge`):
export * from './app';
export * from './preferences';
export * from './notifications';

// This phase's 44 converted modules, alphabetical by store id (matches STORES.md):
export * from './aiPlaygroundStore';
export * from './assistant';
export * from './audio';
export * from './auth';
export * from './behaviour';
export * from './bots';
export * from './changelog';
export * from './channels';
export * from './channelsettings';
export * from './clipboard';
export * from './componentData';
export * from './content';
export * from './contentQueue';
export * from './conversational';
export * from './copyPaste';
export * from './dashboard';
export * from './dateRange';
export * from './demo';
export * from './dialogs';
export * from './exportStats';
export * from './forms';
export * from './formsEditor';
export * from './glimpseContent';
export * from './hubBoxExpand';
export * from './hubUi';
export * from './inactivityTracker';
export * from './integrations';
export * from './intentNew';
export * from './intents';
export * from './intentsList';
export * from './knowledgeBase';
export * from './livechat';
export * from './livechatManagement';
export * from './notificationNative';
export * from './preview';
export * from './productAdvisor';
export * from './qm';
export * from './search';
export * from './stats';
export * from './teasers';
export * from './topicAssistant';
export * from './trainingdata';
export * from './translation';
export * from './welcome';

// Also re-export the legacy bridge itself, so `@/stores` is a one-stop import for both the
// store composables and the bridge functions (`registerAllLegacyStores`, `commitLegacyMutation`,
// `resolveLegacyNamespace`).
export * from './legacyBridge';

// --- Legacy bridge registration --------------------------------------------------------------

import { registerLegacyStores } from './legacyBridge';

import { useAiPlaygroundStore } from './aiPlaygroundStore';
import { useAssistantStore } from './assistant';
import { useAudioStore } from './audio';
import { useAuthStore } from './auth';
import { useBehaviourStore } from './behaviour';
import { useBotsStore } from './bots';
import { useChangelogStore } from './changelog';
import { useChannelsStore } from './channels';
import { useChannelsettingsStore } from './channelsettings';
import { useClipboardStore } from './clipboard';
import { useComponentDataStore } from './componentData';
import { useContentStore } from './content';
import { useContentQueueStore } from './contentQueue';
import { useConversationalStore } from './conversational';
import { useCopyPasteStore } from './copyPaste';
import { useDashboardStore } from './dashboard';
import { useDateRangeStore } from './dateRange';
import { useDemoStore } from './demo';
import { useDialogsStore } from './dialogs';
import { useExportStatsStore } from './exportStats';
import { useFormsStore } from './forms';
import { useFormsEditorStore } from './formsEditor';
import { useGlimpseContentStore } from './glimpseContent';
import { useHubBoxExpandStore } from './hubBoxExpand';
import { useHubUiStore } from './hubUi';
import { useInactivityTrackerStore } from './inactivityTracker';
import { useIntegrationsStore } from './integrations';
import { useIntentNewStore } from './intentNew';
import { useIntentsStore } from './intents';
import { useIntentsListStore } from './intentsList';
import { useKnowledgeBaseStore } from './knowledgeBase';
import { useLivechatStore } from './livechat';
import { useLivechatManagementStore } from './livechatManagement';
import { useNotificationNativeStore } from './notificationNative';
import { usePreviewStore } from './preview';
import { useProductAdvisorStore } from './productAdvisor';
import { useQmStore } from './qm';
import { useSearchStore } from './search';
import { useStatsStore } from './stats';
import { useTeasersStore } from './teasers';
import { useTopicAssistantStore } from './topicAssistant';
import { useTrainingdataStore } from './trainingdata';
import { useTranslationStore } from './translation';
import { useWelcomeStore } from './welcome';

let legacyStoresRegistered = false;

/**
 * Registers every legacy vuex namespace (all 44 modules converted in this phase) with
 * `src/stores/legacyBridge.ts`'s registry, so `commitLegacyMutation('<ns>/<type>', payload)` —
 * used by `src/utils/iframeAPI.ts` for the incoming `vuexStore:mutation:write` postMessage — can
 * resolve any legacy namespace to its Pinia store + action.
 *
 * Does NOT re-register `app`, `preferences`, `notifications` — `./legacyBridge` already
 * registers those three eagerly at module-evaluation time (Phase 1); calling this function does
 * not touch or overwrite those entries.
 *
 * Idempotent: safe to call more than once (e.g. from a hot-reloaded `main.ts`) — repeat calls are
 * no-ops after the first.
 *
 * Call once from `src/main.ts`, after `app.use(pinia)`.
 */
export function registerAllLegacyStores(): void {
  if (legacyStoresRegistered) return;
  legacyStoresRegistered = true;

  registerLegacyStores({
    aiPlaygroundStore: useAiPlaygroundStore,
    assistant: useAssistantStore,
    audio: useAudioStore,
    auth: useAuthStore,
    behaviour: useBehaviourStore,
    bots: useBotsStore,
    changelog: useChangelogStore,
    channels: useChannelsStore,
    // Legacy vuex registration key is lowercase `channelsettings` (NOT `channelSettings`, despite
    // the legacy source filename `modules/channelSettings.ts`) — see STORES.md.
    channelsettings: useChannelsettingsStore,
    clipboard: useClipboardStore,
    componentData: useComponentDataStore,
    // Legacy vuex registration key `content` maps to legacy source file `modules/contentStore.ts`
    // (NOT `modules/content.ts`, which is namespace `glimpseContent` below) — see STORES.md.
    content: useContentStore,
    contentQueue: useContentQueueStore,
    conversational: useConversationalStore,
    copyPaste: useCopyPasteStore,
    dashboard: useDashboardStore,
    dateRange: useDateRangeStore,
    demo: useDemoStore,
    dialogs: {
      useStore: useDialogsStore,
      renames: {
        // Legacy ACTION `chat` collides with the `chat` state prop (the getter of the same name
        // also collides and was renamed `chatGetter`, but getter renames don't affect this
        // mutation/action bridge). See src/stores/dialogs.ts + RENAMES.md.
        chat: 'chatAction',
      },
    },
    exportStats: useExportStatsStore,
    forms: useFormsStore,
    formsEditor: useFormsEditorStore,
    // Legacy vuex registration key `glimpseContent` maps to legacy source file
    // `modules/content.ts` (NOT `modules/contentStore.ts`, which is namespace `content` above).
    glimpseContent: {
      useStore: useGlimpseContentStore,
      renames: {
        // Legacy ACTION `getContent` collides with the getter of the same name (the getter keeps
        // the name; the action is renamed). See src/stores/glimpseContent.ts + RENAMES.md.
        getContent: 'getContentAction',
      },
    },
    hubBoxExpand: useHubBoxExpandStore,
    hubUi: useHubUiStore,
    inactivityTracker: useInactivityTrackerStore,
    integrations: useIntegrationsStore,
    intentNew: useIntentNewStore,
    intents: useIntentsStore,
    intentsList: useIntentsListStore,
    knowledgeBase: useKnowledgeBaseStore,
    livechat: useLivechatStore,
    livechatManagement: useLivechatManagementStore,
    notificationNative: useNotificationNativeStore,
    preview: usePreviewStore,
    productAdvisor: useProductAdvisorStore,
    qm: useQmStore,
    search: useSearchStore,
    stats: useStatsStore,
    teasers: useTeasersStore,
    topicAssistant: useTopicAssistantStore,
    trainingdata: useTrainingdataStore,
    translation: useTranslationStore,
    welcome: useWelcomeStore,
  });
}
